import { useEffect, useRef, useState, type ReactNode } from "react";
import { Printer, RefreshCw } from "lucide-react";
import { ReportPrintFrame } from "@/components/report-print/ReportPrintFrame";
import { ReportPreview } from "@/components/report-print/ReportPreview";
import { ExportDialogFrame } from "@/components/report-print/ExportDialogFrame";
import { checkReportPrintReady } from "@/components/report-print/reportPrintGuard";
import { getMonthlySupplierSummaryData } from "@/features/supplier/api";
import type { MonthlySupplierSummaryDocument } from "@/features/supplier/types";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { todaySaoPaulo } from "@/lib/dates";
import { MonthlySupplierSummaryPrintDocument } from "./MonthlySupplierSummaryPrintDocument";
import { ReportErrorMessage, type ReportErrorCode } from "./ReportErrorMessage";

interface MonthlySupplierSummaryModalProps {
  open: boolean;
  onClose: () => void;
  loadSummaryData?: typeof getMonthlySupplierSummaryData;
}

interface SummaryError {
  code: ReportErrorCode;
  detail: string;
}

function currentMonthStr() {
  return todaySaoPaulo().slice(0, 7);
}

export function MonthlySupplierSummaryModal({
  open,
  onClose,
  loadSummaryData = getMonthlySupplierSummaryData,
}: MonthlySupplierSummaryModalProps) {
  const requestRef = useRef(0);
  const reportPrintFrameRef = useRef<HTMLIFrameElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [month, setMonth] = useState(currentMonthStr);
  const [commissionRate, setCommissionRate] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(true);
  const [document, setDocument] =
    useState<MonthlySupplierSummaryDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SummaryError | null>(null);

  useDialogFocus(dialogRef, onClose, { enabled: open });

  useEffect(() => {
    if (!open) {
      requestRef.current += 1;
      setMonth(currentMonthStr());
      setCommissionRate("");
      setIncludeEmpty(true);
      setDocument(null);
      setError(null);
      setLoading(false);
    }

    return () => {
      requestRef.current += 1;
    };
  }, [open]);

  function isStaleRequest(requestId: number) {
    return requestId !== requestRef.current;
  }

  function invalidatePreview() {
    requestRef.current += 1;
    setDocument(null);
    setError(null);
    setLoading(false);
  }

  function handleMonthChange(value: string) {
    invalidatePreview();
    setMonth(value);
  }

  function handleCommissionRateChange(value: string) {
    invalidatePreview();
    setCommissionRate(value);
  }

  async function fetchSummary() {
    const requestId = ++requestRef.current;
    const requestMonth = month;
    const requestCommissionRate = commissionRate;

    setLoading(true);
    setDocument(null);
    setError(null);

    try {
      const nextDocument = await loadSummaryData(
        requestMonth,
        requestCommissionRate,
      );
      if (isStaleRequest(requestId)) return;

      setDocument(nextDocument);
    } catch {
      if (isStaleRequest(requestId)) return;

      setError({
        code: "EXP-GERAR",
        detail:
          "Não foi possível gerar o resumo mensal. Confira os filtros e tente novamente.",
      });
    } finally {
      if (!isStaleRequest(requestId)) setLoading(false);
    }
  }

  function handlePrint() {
    const frameWindow = reportPrintFrameRef.current?.contentWindow;
    if (!frameWindow) {
      setError({
        code: "EXP-PRINT",
        detail: "A prévia ainda não está pronta. Gere o relatório novamente.",
      });
      return;
    }

    const ready = checkReportPrintReady(frameWindow.document);
    if (!ready.ok) {
      setError({ code: "EXP-PRINT", detail: ready.message });
      return;
    }

    setError(null);
    frameWindow.focus();
    frameWindow.print();
  }

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .report-panel-scroll {
          min-height: 0;
          overflow: auto;
          padding: 1rem;
          scrollbar-color: rgba(92, 104, 120, 0.36) rgba(92, 104, 120, 0.09);
          scrollbar-gutter: stable;
          scrollbar-width: thin;
        }
        .report-panel-scroll::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        .report-panel-scroll::-webkit-scrollbar-track {
          background: rgba(92, 104, 120, 0.09);
          border-radius: 4px;
        }
        .report-panel-scroll::-webkit-scrollbar-thumb {
          background: rgba(92, 104, 120, 0.36);
          border-radius: 4px;
        }
        .report-panel-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(92, 104, 120, 0.5);
        }
        .report-param-input {
          width: 100%;
          min-height: 2.75rem;
          padding: 0.7rem 0.85rem;
          background: var(--login-bg);
          border: 1px solid var(--login-border);
          border-radius: 4px;
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          outline: none;
        }
        .report-param-input:focus {
          border-color: var(--login-accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-accent) 16%, transparent);
        }
        .report-primary-btn,
        .report-secondary-btn {
          transition: opacity 0.16s ease, background-color 0.16s ease;
        }
        @media (hover: hover) {
          .report-primary-btn:hover:not(:disabled),
          .report-secondary-btn:hover:not(:disabled) { filter: brightness(0.96); }
        }
        .report-primary-btn:disabled,
        .report-secondary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .report-iframe-spin { animation: spin 0.8s linear infinite; }
        .report-print-frame {
          background: #eef2f7;
          border: 0;
          border-radius: 4px;
          display: block;
          height: min(72vh, 820px);
          width: 100%;
        }
        @media print {
          .report-overlay { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .report-primary-btn,
          .report-secondary-btn,
          .report-iframe-spin {
            animation: none !important;
            transition-duration: 0.01ms !important;
            transform: none !important;
          }
        }
      `}</style>

      <ExportDialogFrame
        titleId="monthly-supplier-summary-modal-title"
        eyebrow="Resumo mensal"
        title="Consolidado geral"
        onClose={onClose}
        dialogRef={dialogRef}
        overlayClassName="report-overlay"
        panelClassName="report-panel"
      >
        <div
          className="report-panel-scroll"
          style={{
            display: "grid",
            gap: "1rem",
            minHeight: 0,
            overflow: "auto",
            padding: "1rem",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "var(--login-muted)",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Gere a prévia consolidada dos fornecedores no período selecionado.
          </p>

          <div style={{ display: "grid", gap: "0.75rem" }}>
            <Field label="Mês de referência">
              <input
                className="report-param-input"
                type="month"
                value={month}
                onInput={(event) =>
                  handleMonthChange(event.currentTarget.value)
                }
                onChange={(event) => handleMonthChange(event.target.value)}
              />
            </Field>
            <Field label="Comissão (%)">
              <input
                className="report-param-input"
                type="text"
                inputMode="decimal"
                value={commissionRate}
                onInput={(event) =>
                  handleCommissionRateChange(event.currentTarget.value)
                }
                onChange={(event) =>
                  handleCommissionRateChange(event.target.value)
                }
              />
            </Field>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.55rem",
                color: "var(--login-text)",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              <input
                checked={includeEmpty}
                type="checkbox"
                onChange={(event) => setIncludeEmpty(event.target.checked)}
              />
              Incluir fornecedores sem movimento
            </label>
          </div>

          {error && (
            <ReportErrorMessage code={error.code} detail={error.detail} />
          )}

          {document && (
            <ReportPrintFrame
              frameRef={reportPrintFrameRef}
              title={`Prévia do resumo ${document.number}`}
            >
              <ReportPreview>
                <MonthlySupplierSummaryPrintDocument
                  document={document}
                  includeEmpty={includeEmpty}
                />
              </ReportPreview>
            </ReportPrintFrame>
          )}

          <div style={{ display: "grid", gap: "0.65rem" }}>
            <button
              type="button"
              className="report-primary-btn"
              onClick={fetchSummary}
              disabled={loading}
              style={primaryButtonStyle}
            >
              {loading ? (
                <RefreshCw size={15} className="report-iframe-spin" />
              ) : (
                <RefreshCw size={15} />
              )}
              {loading ? "Gerando..." : "Gerar prévia"}
            </button>

            {document && (
              <button
                type="button"
                className="report-secondary-btn"
                onClick={handlePrint}
                style={secondaryButtonStyle}
              >
                <Printer size={15} />
                Imprimir / PDF
              </button>
            )}
          </div>
        </div>
      </ExportDialogFrame>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: "0.4rem" }}>
      <span
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: "0.85rem",
          letterSpacing: "0.05em",
          color: "var(--login-text)",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const primaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.55rem",
  minHeight: "2.75rem",
  border: "none",
  borderRadius: "4px",
  background: "var(--login-accent)",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
} as const;

const secondaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.55rem",
  minHeight: "2.75rem",
  borderRadius: "4px",
  border:
    "1px solid color-mix(in srgb, var(--login-accent) 38%, var(--login-border))",
  background: "transparent",
  color: "var(--login-accent)",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "0.95rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
} as const;
